import React, { useState, useEffect } from 'react';
import { Play, BookOpen, Users, Calendar, Download, Gift, Bell, TrendingUp, Award, Upload, X, Sparkles, Edit3, FileText, Video } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import PersonalGoals from './PersonalGoals';
import AssignmentsWidget from './AssignmentsWidget';
import TourSystem, { TourSelector, EnhancedTourButton } from './TourSystem';
import PushNotificationBanner from './PushNotificationBanner';

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [accessLevel, setAccessLevel] = useState(null);
  const [showTour, setShowTour] = useState(false);
  const [showTourSelector, setShowTourSelector] = useState(false);
  const [tourType, setTourType] = useState('student');
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [courseProgress, setCourseProgress] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState(true);
  const [welcomeMessage, setWelcomeMessage] = useState({
    title: "Welcome Message from Pastor Obed",
    message: "You are being shaped for greatness. Every lesson, every challenge, every breakthrough is preparing you for your divine assignment.",
    author: "Pastor Obed Ansu"
  });
  const [showEditWelcome, setShowEditWelcome] = useState(false);
  const [editingWelcome, setEditingWelcome] = useState({ title: '', message: '', author: '' });
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);

  // Fetch dynamic notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('access_token');
        const response = await fetch(`${backendUrl}/api/notifications/dashboard`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          // Transform to notification format
          const formattedNotifications = (data.notifications || []).map(n => ({
            id: n.id,
            type: n.type,
            title: n.title,
            message: n.message,
            time: n.priority === 'urgent' ? '🔴 Urgent' : (n.priority === 'high' ? '⚠️ Important' : ''),
            read: false,
            action_url: n.action_url,
            icon: n.icon,
            priority: n.priority
          }));
          setNotifications(formattedNotifications);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
        // Fallback to empty
        setNotifications([]);
      } finally {
        setLoadingNotifications(false);
      }
    };
    
    fetchNotifications();
    // Refresh every 5 minutes
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const quickActions = [
    {
      title: 'Continue Learning',
      description: 'Resume your current course',
      icon: Play,
      href: '/courses',
      color: 'purple',
      progress: 65
    },
    {
      title: 'Join Live Session',
      description: 'Next session in 2 days',
      icon: Calendar,
      href: '/live-sessions',
      color: 'orange',
      badge: 'Upcoming'
    },
    {
      title: 'My Submissions',
      description: 'View feedback on your work',
      icon: FileText,
      href: '/my-submissions',
      color: 'purple'
    },
    {
      title: 'Watch Recordings',
      description: 'Catch up on past sessions',
      icon: Video,
      href: '/recordings',
      color: 'gray',
      badge: 'New'
    },
    {
      title: 'My Certificates',
      description: 'Download your achievements',
      icon: Award,
      href: '/my-certificates',
      color: 'orange'
    },
    {
      title: 'Download Ebooks',
      description: '3 new resources available',
      icon: Download,
      href: '/library',
      color: 'gray',
      badge: '3'
    }
  ];

  useEffect(() => {
    fetchAccessLevel();
    fetchEnrolledCourses();
    
    // Check if user should see the tour (first login)
    const hasSeenStudentTour = localStorage.getItem('student_tour_completed');
    if (!hasSeenStudentTour) {
      // Small delay so dashboard loads first
      setTimeout(() => {
        setTourType('student');
        setShowTour(true);
      }, 1000);
    }
  }, []);

  // Handle tour selection
  const handleSelectTour = (type) => {
    setTourType(type);
    setShowTourSelector(false);
    setShowTour(true);
  };

  // Get user role for tour access
  const getUserRole = () => {
    if (user?.role === 'admin' || user?.is_admin) return 'admin';
    if (user?.role === 'mentor' || user?.is_mentor) return 'mentor';
    return 'user';
  };

  const fetchAccessLevel = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/access/level`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAccessLevel(data);
      }
    } catch (error) {
      console.error('Error fetching access level:', error);
    }
  };

  const fetchWelcomeMessage = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/site-settings/welcome-message`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.message) {
          setWelcomeMessage(data);
        }
      }
    } catch (error) {
      console.error('Error fetching welcome message:', error);
    }
  };

  const handleEditWelcome = () => {
    setEditingWelcome({
      title: welcomeMessage.title,
      message: welcomeMessage.message,
      author: welcomeMessage.author
    });
    setShowEditWelcome(true);
  };

  const handleSaveWelcome = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/site-settings/welcome-message`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(editingWelcome)
      });
      
      if (response.ok) {
        const data = await response.json();
        setWelcomeMessage(data.data);
        setShowEditWelcome(false);
      }
    } catch (error) {
      console.error('Error saving welcome message:', error);
    }
  };

  const fetchEnrolledCourses = async () => {
    try {
      setLoadingProgress(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      // Fetch enrolled courses
      const coursesResponse = await fetch(`${backendUrl}/api/courses/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (coursesResponse.ok) {
        const courses = await coursesResponse.json();
        setEnrolledCourses(courses);
        
        // Fetch progress for each enrolled course
        const progressPromises = courses.slice(0, 4).map(async (course) => {
          try {
            const progressResponse = await fetch(
              `${backendUrl}/api/course-progress/${course.id}`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (progressResponse.ok) {
              const progressData = await progressResponse.json();
              return {
                id: course.id,
                title: course.title,
                progress: progressData.progress_percentage || 0,
                currentModule: progressData.current_lesson_title || 'Not started',
                totalModules: course.lessons_count || 0,
                completedModules: progressData.completed_lessons || 0
              };
            }
          } catch (err) {
            console.error(`Error fetching progress for ${course.title}:`, err);
          }
          
          return {
            id: course.id,
            title: course.title,
            progress: 0,
            currentModule: 'Not started',
            totalModules: course.lessons_count || 0,
            completedModules: 0
          };
        });
        
        const progressResults = await Promise.all(progressPromises);
        setCourseProgress(progressResults.filter(Boolean));
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoadingProgress(false);
    }
  };

  // Fetch welcome message on mount
  useEffect(() => {
    fetchWelcomeMessage();
  }, []);

  const isMember = accessLevel?.is_mentorship_member || false;
  const isAdmin = user?.role === 'admin' || user?.is_admin;

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Tour System */}
      <TourSystem 
        tourType={tourType} 
        showTour={showTour} 
        setShowTour={setShowTour} 
      />
      
      {/* Tour Selector Modal */}
      <TourSelector 
        isOpen={showTourSelector}
        onClose={() => setShowTourSelector(false)}
        onSelectTour={handleSelectTour}
        userRole={getUserRole()}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Push Notification Opt-In Banner */}
        <PushNotificationBanner />
        
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8 flex items-start justify-between"
        >
          <div>
            <h1 className="text-3xl font-light text-black mb-2">
              Welcome back, {user?.profile?.first_name || 'Mentee'}!
            </h1>
            <p className="text-gray-600">
              Continue your journey of transformation and Kingdom impact
            </p>
          </div>
          <EnhancedTourButton onClick={() => setShowTourSelector(true)} />
        </motion.div>

        {/* CODEX promo tile — opens game in new tab */}
        <motion.a
          href="/codex/dashboard"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="dashboard-codex-promo"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.4 }}
          whileHover={{ y: -2 }}
          className="block mb-6 relative overflow-hidden rounded-2xl p-5 sm:p-6 cursor-pointer no-underline"
          style={{
            background: 'linear-gradient(135deg, #1a0e2c 0%, #4c1d95 60%, #7c3aed 100%)',
            border: '1px solid rgba(212,175,55,0.4)',
            color: '#fdf6e3',
            boxShadow: '0 8px 32px rgba(76, 29, 149, 0.4)',
          }}
        >
          <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full opacity-30" style={{ background: 'radial-gradient(circle, #d4af37, transparent 70%)' }} />
          <div className="relative flex items-center gap-4 flex-wrap">
            <div className="text-3xl sm:text-4xl">📜</div>
            <div className="flex-1 min-w-[200px]">
              <div className="text-xs tracking-[0.3em] text-amber-300/80 mb-1" style={{ fontFamily: "'Cinzel', serif" }}>
                NEW · OPENS IN A NEW TAB
              </div>
              <div className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "'Cinzel', serif", color: '#f5d97c' }}>
                CODEX: The Ancient Scrolls
              </div>
              <div className="text-sm text-purple-100 mt-1">
                Decode Hebrew, Greek and the prophetic mysteries — one daily scroll at a time.
              </div>
            </div>
            <div className="px-4 py-2 rounded-lg font-bold text-sm" style={{ background: 'linear-gradient(135deg, #d4af37, #ea580c)', color: '#1a0e2c', fontFamily: "'Cinzel', serif", letterSpacing: '0.1em' }}>
              BEGIN →
            </div>
          </div>
        </motion.a>

        {/* Weekly Encouragement */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-3xl p-8 mb-8 text-white relative overflow-hidden"
        >
          {isAdmin && (
            <button 
              onClick={handleEditWelcome}
              className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              title="Edit Welcome Message"
            >
              <Edit3 className="w-5 h-5" />
            </button>
          )}
          <div className="flex-1">
            <h2 className="text-2xl font-semibold mb-2">
              {welcomeMessage.title}
            </h2>
            <p className="text-purple-100 mb-4">
              "{welcomeMessage.message}"
            </p>
            <p className="text-sm text-purple-200">- {welcomeMessage.author}</p>
            {welcomeMessage.updated_at && (
              <p className="text-xs text-purple-300 mt-2">
                Updated: {new Date(welcomeMessage.updated_at).toLocaleDateString()}
              </p>
            )}
          </div>
          {/* Decorative pulse */}
          <motion.div
            aria-hidden
            className="absolute -right-12 -bottom-12 w-48 h-48 rounded-full bg-white/10 pointer-events-none"
            animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.6, 0.4] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          />
        </motion.div>

        {/* Edit Welcome Message Modal */}
        {showEditWelcome && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">Edit Welcome Message</h2>
                <button 
                  onClick={() => setShowEditWelcome(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={editingWelcome.title}
                    onChange={(e) => setEditingWelcome({...editingWelcome, title: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Welcome Message from Pastor Obed"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <textarea
                    value={editingWelcome.message}
                    onChange={(e) => setEditingWelcome({...editingWelcome, message: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent h-32 resize-none"
                    placeholder="Your weekly encouragement message..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Author</label>
                  <input
                    type="text"
                    value={editingWelcome.author}
                    onChange={(e) => setEditingWelcome({...editingWelcome, author: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Pastor Obed Ansu"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowEditWelcome(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveWelcome}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Save Message
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <BookOpen className="w-8 h-8 text-purple-600" />
              <span className="text-2xl font-bold text-black">{enrolledCourses.length || 0}</span>
            </div>
            <p className="text-gray-600 font-medium">Enrolled Courses</p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-orange-600" />
              <span className="text-2xl font-bold text-black">
                {courseProgress.length > 0 
                  ? Math.round(courseProgress.reduce((sum, c) => sum + c.progress, 0) / courseProgress.length)
                  : 0}%
              </span>
            </div>
            <p className="text-gray-600 font-medium">Overall Progress</p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <Award className="w-8 h-8 text-purple-600" />
              <span className="text-2xl font-bold text-black">{user?.points || 0}</span>
            </div>
            <p className="text-gray-600 font-medium">Academy Points</p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8 text-orange-600" />
              <span className="text-sm font-bold text-black bg-orange-100 px-2 py-1 rounded-full">
                {user?.mentorship_level || 'Disciple'}
              </span>
            </div>
            <p className="text-gray-600 font-medium">Current Level</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Quick Actions */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-black mb-6">
                Quick Actions
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {quickActions.map((action, index) => {
                  const IconComponent = action.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.06, duration: 0.3 }}
                      whileHover={{ y: -3 }}
                      onClick={() => navigate(action.href)}
                      className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:border-purple-200 transition-all duration-300 cursor-pointer group"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className={`p-3 rounded-xl transition-colors duration-300 ${
                          action.color === 'purple' ? 'bg-purple-100 group-hover:bg-purple-200' : 
                          action.color === 'orange' ? 'bg-orange-100 group-hover:bg-orange-200' : 'bg-gray-100 group-hover:bg-gray-200'
                        }`}>
                          <IconComponent className={`w-6 h-6 ${
                            action.color === 'purple' ? 'text-purple-600' :
                            action.color === 'orange' ? 'text-orange-600' : 'text-gray-600'
                          }`} />
                        </div>
                        {action.badge && (
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                            action.color === 'purple' ? 'bg-purple-100 text-purple-800' :
                            action.color === 'orange' ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {action.badge}
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-black mb-1 group-hover:text-purple-600 transition-colors">
                        {action.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4">
                        {action.description}
                      </p>
                      {action.progress && (
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <motion.div 
                            initial={{ width: 0 }}
                            animate={{ width: `${action.progress}%` }}
                            transition={{ delay: 0.4 + index * 0.06, duration: 0.6, ease: 'easeOut' }}
                            className="bg-purple-600 h-2 rounded-full group-hover:bg-purple-700"
                          />
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Personal Goals & Objectives */}
            <div className="mb-8">
              <PersonalGoals />
            </div>

            {/* Assignments & Deadlines */}
            <AssignmentsWidget />

            {/* Course Progress - Members Only */}
            {isMember ? (
            <div>
              <h2 className="text-2xl font-semibold text-black mb-6">
                Your Academy Journey
              </h2>
              
              {loadingProgress ? (
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-500">Loading your progress...</p>
                </div>
              ) : courseProgress.length > 0 ? (
                <div className="space-y-6">
                  {courseProgress.map((course, index) => (
                    <div key={index} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-black">
                          {course.title}
                        </h3>
                        <span className="text-sm font-semibold text-purple-600">
                          {course.progress}% Complete
                        </span>
                      </div>
                      
                      <div className="mb-4">
                        <div className="flex justify-between text-sm text-gray-600 mb-2">
                          <span>Current: {course.currentModule}</span>
                          <span>{course.completedModules}/{course.totalModules} lessons</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className="bg-gradient-to-r from-purple-600 to-purple-700 h-3 rounded-full transition-all duration-300" 
                            style={{ width: `${course.progress}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <button 
                        onClick={() => navigate(`/courses/${course.id}`)}
                        className="text-purple-600 hover:text-purple-700 font-medium text-sm flex items-center space-x-2"
                      >
                        <span>{course.progress > 0 ? 'Continue Learning' : 'Start Course'}</span>
                        <Play className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
                  <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">No Courses Started Yet</h3>
                  <p className="text-gray-500 mb-4">Begin your journey by enrolling in a course</p>
                  <button 
                    onClick={() => navigate('/courses')}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Browse Courses
                  </button>
                </div>
              )}
            </div>
            ) : (
              <div className="bg-gradient-to-r from-purple-50 to-orange-50 rounded-2xl p-8 border-2 border-purple-200">
                <div className="text-center">
                  <Award className="w-16 h-16 text-purple-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2">Unlock Your Academy Journey</h3>
                  <p className="text-gray-600 mb-6">
                    Track your progress, earn points, and see your academy level by enrolling in an academy track.
                  </p>
                  <button
                    onClick={() => navigate('/pricing')}
                    className="bg-black text-white px-8 py-3 rounded-xl font-semibold hover:bg-gray-800 transition-colors"
                  >
                    View Payment Plans
                  </button>
                  <p className="text-sm text-gray-500 mt-2">From £120/month or £1,440 upfront</p>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Notifications */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-black">Notifications</h3>
                <Bell className="w-5 h-5 text-gray-400" />
              </div>
              
              <div className="space-y-3">
                {loadingNotifications ? (
                  <div className="text-center py-4 text-gray-500 text-sm">Loading...</div>
                ) : notifications.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No new notifications
                  </div>
                ) : (
                  notifications.slice(0, 5).map((notification) => (
                    <div 
                      key={notification.id} 
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        notification.priority === 'urgent' ? 'bg-red-50 border-red-200 hover:bg-red-100' :
                        notification.priority === 'high' ? 'bg-orange-50 border-orange-200 hover:bg-orange-100' :
                        'bg-purple-50 border-purple-200 hover:bg-purple-100'
                      }`}
                      onClick={() => notification.action_url && (window.location.href = notification.action_url)}
                    >
                      <h4 className="font-medium text-black text-sm mb-1">
                        {notification.title}
                      </h4>
                      <p className="text-gray-600 text-xs mb-2">
                        {notification.message}
                      </p>
                      {notification.time && (
                        <span className="text-xs text-gray-500">
                          {notification.time}
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
              
              <button 
                onClick={() => window.location.href = '/notifications'}
                className="w-full mt-4 text-purple-600 hover:text-purple-700 font-medium text-sm"
              >
                View All Notifications
              </button>
            </div>

            {/* Upload Documents */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-black">Submit Your Work</h3>
                <Upload className="w-5 h-5 text-gray-400" />
              </div>
              
              <p className="text-gray-600 text-sm mb-4">
                Upload homework, projects, and assignments for review
              </p>
              
              <button 
                onClick={() => window.location.href = '/uploads'}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-xl font-medium text-sm transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <Upload className="w-4 h-4" />
                <span>Upload Document</span>
              </button>
            </div>

            {/* Support */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 text-white">
              <h3 className="font-semibold mb-2">
                Need Help?
              </h3>
              <p className="text-orange-100 text-sm mb-4">
                Chat with The Vine Voice, your AI assistant, for instant support.
              </p>
              <button 
                onClick={() => {
                  // Trigger the Vine Voice bubble (replaces the old FloatingAIButton)
                  const vineBubble = document.querySelector('[data-testid="vine-voice-bubble"]')
                    || document.querySelector('[data-testid="vine-voice-trigger"]');
                  if (vineBubble) vineBubble.click();
                }}
                className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full font-medium text-sm hover:bg-white/30 transition-colors duration-200"
              >
                Ask The Vine Voice
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;